import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { useForm } from 'react-hook-form'

import type { ParsedTemplateVariable } from '@/components/organisms/template-variable-editor/template-variable.interface'

import { deduplicateVariableList } from './template-variable-deduplicator.util'
import type { TemplateVariableFormValues } from './template-variable-form.interface'
import {
  buildTemplateVariableDefaultValues,
  buildTemplateVariableSchema,
} from './template-variable-schema-builder.util'

interface UseTemplateVariableFormReturn {
  form: UseFormReturn<TemplateVariableFormValues>
  uniqueVariableList: ParsedTemplateVariable[]
}

/**
 * Wraps react-hook-form with dynamic Zod schema generation from template variables.
 * Deduplicates variables by name and rebuilds the schema on variable changes.
 */
export const useTemplateVariableForm = (
  variableList: ParsedTemplateVariable[]
): UseTemplateVariableFormReturn => {
  const uniqueVariableList = useMemo(() => deduplicateVariableList(variableList), [variableList])

  const schema = useMemo(
    () => buildTemplateVariableSchema(uniqueVariableList),
    [uniqueVariableList]
  )

  const defaultValues = useMemo(
    () => buildTemplateVariableDefaultValues(uniqueVariableList),
    [uniqueVariableList]
  )

  const form = useForm<TemplateVariableFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
  })

  return { form, uniqueVariableList }
}
